/**
 * State Autocomplete Component
 *
 * Fuzzy search autocomplete for US state selection
 */

"use client";

import { storefrontStateData } from "@/components/InsuranceStepper/InsuranceStepper.constants";
import {
  Command,
  CommandEmpty,
  CommandItem,
} from "@frontend/shared/components/ui/command";
import { Input } from "@frontend/shared/components/ui/input";
import { cn } from "@frontend/shared/lib/utils";
import Fuse from "fuse.js";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface StateAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  /** Override the default input className. Pass `null` to keep the default. */
  className?: string;
  /** Whether to focus the input on mount. Defaults to `true` for backward compat. */
  autoFocus?: boolean;
  /**
   * Selectable states. Defaults to the shop-servable list
   * (`storefrontStateData`) for residence pickers; pass `insuredStateData` for
   * insured/mailing-address inputs, which may be US territories.
   */
  options?: { value: string; label: string }[];
}

export function StateAutocomplete({
  value,
  onChange,
  id,
  className,
  autoFocus = true,
  options = storefrontStateData,
}: StateAutocompleteProps) {
  const t = useTranslations("InsuranceConnector");
  const [isOpen, setIsOpen] = useState(false);
  const [dropDirection, setDropDirection] = useState<"down" | "up">("down");
  const [searchValue, setSearchValue] = useState(() => {
    if (value) {
      const state = options.find((s) => s.value === value);
      return state ? state.label : "";
    }
    return "";
  });
  const inputRef = useRef<HTMLInputElement>(null);

  // Estimated dropdown height: max-h-[200px] + 1rem offset.
  const DROPDOWN_BUDGET_PX = 220;
  const computeDropDirection = useCallback(() => {
    const rect = inputRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    setDropDirection(spaceBelow < DROPDOWN_BUDGET_PX ? "up" : "down");
  }, []);

  // Configure Fuse for fuzzy search
  const fuseOptions = {
    keys: ["label", "value"],
    threshold: 0.3,
    distance: 50,
    minMatchCharLength: 1,
    shouldSort: true,
    includeScore: true,
  };

  const fuse = useMemo(() => new Fuse(options, fuseOptions), [options]);

  // Cache search results
  const searchCacheRef = useRef<{
    search: string;
    results: Array<{ item: { value: string; label: string }; score?: number }>;
  } | null>(null);

  // Filter function for fuzzy search
  const fuseFilter = useCallback(
    (stateItem: string, search: string) => {
      if (!search) return 1;

      const searchLower = search.toLowerCase();
      const [stateName, stateCode] = stateItem.split("|");

      // Exact prefix match gets highest score (for both name and code)
      if (
        stateName.toLowerCase().startsWith(searchLower) ||
        stateCode.toLowerCase().startsWith(searchLower)
      ) {
        return 1;
      }

      // Use cached fuzzy search results
      let results =
        searchCacheRef.current?.search === search
          ? searchCacheRef.current.results
          : null;

      if (!results) {
        results = fuse.search(search);
        searchCacheRef.current = { search, results };
      }

      const match = results.find(
        (result) =>
          result.item.label.toLowerCase() === stateName.toLowerCase() ||
          result.item.value.toLowerCase() === stateCode.toLowerCase(),
      );

      return match?.score ? 1 - match.score : 0;
    },
    [fuse],
  );

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setIsOpen(false);
    }, 200);
  }, []);

  const handleSelect = useCallback(
    (state: { value: string; label: string }) => {
      onChange(state.value);
      setSearchValue(state.label);
      setIsOpen(false);
    },
    [onChange],
  );

  // Auto-focus on mount (opt-out for surfaces that don't want focus stolen).
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Auto-select on exact match (case-insensitive) for both full name and abbreviation
  useEffect(() => {
    if (!searchValue) return;

    const exactMatch = options.find(
      (state) =>
        state.label.toLowerCase() === searchValue.toLowerCase() ||
        state.value.toLowerCase() === searchValue.toLowerCase(),
    );

    if (exactMatch) {
      onChange(exactMatch.value);
    } else {
      // Clear selection if no exact match
      if (value) {
        onChange("");
      }
    }
  }, [searchValue, onChange, value, options]);

  return (
    <div className="relative w-full">
      <Command
        filter={fuseFilter}
        className="rounded-none bg-transparent"
        shouldFilter={isOpen}
      >
        <Input
          ref={inputRef}
          id={id}
          className={cn(
            "h-auto rounded-full py-[31px] w-full px-6 border-transparent focus:border-transparent focus:ring-1 focus:ring-[#00A3FF] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#00A3FF] focus-visible:ring-offset-0",
            className,
          )}
          placeholder={t("TypeToSelect")}
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            if (value) {
              onChange("");
            }
            computeDropDirection();
            setIsOpen(true);
          }}
          onFocus={() => {
            computeDropDirection();
            setIsOpen(true);
          }}
          onBlur={handleBlur}
        />
        {isOpen && (
          <div
            className={cn(
              "absolute left-0 right-0 bg-white overflow-y-auto max-h-[200px] shadow-lg border border-gray-200 rounded-[24px] z-50",
              dropDirection === "down"
                ? "top-[calc(100%+1rem)]"
                : "bottom-[calc(100%+1rem)]",
            )}
          >
            <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">
              {t("NoStatesFound")}
            </CommandEmpty>
            {options
              .filter(
                (state) =>
                  fuseFilter(`${state.label}|${state.value}`, searchValue) > 0,
              )
              .sort((a, b) => {
                const scoreA = fuseFilter(`${a.label}|${a.value}`, searchValue);
                const scoreB = fuseFilter(`${b.label}|${b.value}`, searchValue);
                return scoreB - scoreA;
              })
              .map((state) => (
                <CommandItem
                  key={state.value}
                  value={`${state.label}|${state.value}`}
                  onSelect={() => handleSelect(state)}
                  className="px-6 py-3 cursor-pointer hover:bg-gray-100 focus:bg-gray-100"
                >
                  {state.label}
                </CommandItem>
              ))}
          </div>
        )}
      </Command>
    </div>
  );
}
